/**
 * ProjectThreadStrip — the "you are in a shared room" bar above a project chat.
 *
 * Two jobs, both of which the chat surface cannot otherwise convey:
 *
 *  1. **Orientation.** A project thread looks exactly like a private one. This
 *     names the project and links back to it, so a member who arrived from a
 *     sidebar link knows the thread is shared before they type into it.
 *  2. **Contributors, additively.** The faces are everyone who has actually
 *     written here, growing as people join in. That is the "see who contributed
 *     what" promise, answered in the place the contributing happens.
 *
 * Contributors are derived from the loaded messages rather than fetched: the
 * transcript is already in memory and already carries authorship, so a separate
 * request would only introduce a way for the two to disagree.
 */

import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '@/hooks/useLanguage';
import { useTenantPath } from '@/routing';
import type { Message } from '@/types/models';
import projectService from '../services/projectService';
import type { ProjectSummary } from '../types';
import { PersonAvatarStack } from './PersonAvatar';
import { ProjectIcon } from './ProjectIcon';
import './ProjectThreadStrip.css';

interface ProjectThreadStripProps {
  projectId: string;
  messages: Message[];
  /**
   * Who may write here. The strip says what the thread IS, so it has to know:
   * telling a reader that everyone "can read and continue this thread" on a
   * thread only one person may continue is worse than saying nothing at all.
   */
  threadMode?: 'group' | 'solo' | null;
  /** True when the viewer is the person a solo thread belongs to. */
  isOwnThread?: boolean;
}

export function ProjectThreadStrip({
  projectId,
  messages,
  threadMode,
  isOwnThread,
}: ProjectThreadStripProps) {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const tenantPath = useTenantPath();
  const [project, setProject] = useState<ProjectSummary | null>(null);

  useEffect(() => {
    let cancelled = false;
    projectService
      .getProject(projectId)
      .then(loaded => { if (!cancelled) setProject(loaded); })
      // Silent: the strip is orientation, not function. If the project can't be
      // read the chat itself still works, and the access error will surface on
      // the project page where it means something.
      .catch(() => undefined);
    return () => { cancelled = true; };
  }, [projectId]);

  const contributors = useMemo(() => {
    const byUser = new Map<string, { id: string; name?: string; email?: string; tenantName?: string }>();
    messages.forEach(message => {
      if (message.role !== 'user' || !message.sender_user_id) return;
      if (byUser.has(message.sender_user_id)) return;
      byUser.set(message.sender_user_id, {
        id: message.sender_user_id,
        name: message.sender_name,
        email: message.sender_email,
        tenantName: message.sender_tenant_name,
      });
    });
    return Array.from(byUser.values());
  }, [messages]);

  return (
    <div className="thread-strip">
      <button
        type="button"
        className="thread-strip-project"
        onClick={() => navigate(tenantPath(`/projects/${projectId}`))}
        title={t('projects.thread.openProject') || 'Open the project'}
      >
        <ProjectIcon strokeWidth={1.8} />
        <span dir="auto" className="thread-strip-name">
          {project?.project_name || (t('projects.thread.shared') || 'Shared project')}
        </span>
      </button>

      <span dir="auto" className="thread-strip-note">
        {threadMode === 'solo'
          ? (isOwnThread
            ? (t('projects.thread.noteSoloMine')
              || 'Everyone in this project can read this thread. Only you can write in it.')
            : (t('projects.thread.noteSolo')
              || 'Everyone in this project can read this thread, but only its author can write in it.'))
          : (t('projects.thread.note')
            || 'Everyone in this project can read and continue this thread.')}
      </span>

      {contributors.length > 0 && (
        <span className="thread-strip-people">
          <span className="thread-strip-label">
            {t('projects.thread.contributors') || 'Contributors'}
          </span>
          <PersonAvatarStack people={contributors} max={4} size={22} />
        </span>
      )}
    </div>
  );
}

export default ProjectThreadStrip;
